import React, { useState, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import screenshotLight from '../assets/images/JDiskMark_benchmark_sample_screenshot-light_theme.jpg';
import screenshotDark  from '../assets/images/JDiskMark_benchmark_sample_screenshot-dark_theme.jpg';
import { useTheme } from '../hooks/useTheme';
import BackToTop from '../components/BackToTop';

const LiveBenchmarkDemo = lazy(() => import('../components/LiveBenchmarkDemo'));

// Production image CDN — hero/feature images are hosted on the main domain
const REMOTE_BASE = 'https://www.jdiskmark.net/images';

export default function UserHomeScreen() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [demoActive, setDemoActive] = useState(false);

  return (
    <div className='home-page'>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className='home-hero'>
        <div className='home-hero__content'>
          <h1 className='home-hero__title'>Welcome to JDiskMark!</h1>
          <p className='home-hero__subtitle'>
            The free cross-platform &amp; open-source Java disk benchmarking tool &amp; community for SSDs &amp; HDDs
          </p>
          <div className='home-hero__cta-group'>
            <Button
              variant='primary'
              size='lg'
              className='home-hero__cta home-hero__cta--primary rounded-pill'
              onClick={() => navigate("/download_jdiskmark_desktop_app")}
            >
              Download Free
            </Button>
            <Button
              variant='outline-light'
              size='lg'
              className='home-hero__cta home-hero__cta--secondary rounded-pill'
              onClick={() => navigate("/all_benchmarks")}
            >
              View Benchmarks
            </Button>
          </div>
        </div>
      </section>

      {/* ── Screenshot + Live Data Overlay ───────────────────── */}
      <section className='home-section home-section--screenshot'>
        <div className='home-screenshot-wrapper'>

          {/* Badge — doubles as a reset control when the demo is active */}
          <div
            className='home-screenshot-badge'
            onClick={demoActive ? () => setDemoActive(false) : undefined}
            style={demoActive ? { cursor: 'pointer', userSelect: 'none' } : {}}
            title={demoActive ? 'Click to reset demo' : undefined}
          >
            {demoActive ? '✕ Live Demo' : 'Sample Benchmark'}
          </div>

          {/* Screenshot — always visible; overlay sits on top */}
          <img
            src={isDark ? screenshotDark : screenshotLight}
            alt="JDiskMark – Sample benchmark screenshot showing read/write speeds on an NVMe SSD"
            className='home-screenshot'
            loading='lazy'
          />

          {/* Start / Cancel button — always rendered at same position */}
          <div className='home-start-btn-wrap'>
            {demoActive ? (
              <button
                id='home-start-demo-btn'
                className='home-cancel-btn'
                disabled
                aria-label='Live demo is running'
              >
                &#9646; Cancel
              </button>
            ) : (
              <>
                <button
                  id='home-start-demo-btn'
                  className='home-start-btn'
                  aria-label='Click to start a live benchmark demo'
                  onClick={() => setDemoActive(true)}
                >
                  &#9654; Start
                </button>
                <div className='home-start-tooltip' role='tooltip'>
                  Click Start for a live demo!
                </div>
              </>
            )}
          </div>

          {/* Live data overlay — rendered on top of the screenshot when active */}
          {demoActive && (
            <Suspense fallback={
              <div className='home-demo-loading-overlay'>
                ⦿ Loading live data…
              </div>
            }>
              <LiveBenchmarkDemo isDark={isDark} />
            </Suspense>
          )}

        </div>
      </section>

      {/* ── Feature Cards ────────────────────────────────────── */}
      <section className='home-section home-section--features'>
        <div className='home-features'>
          {[
            { icon: "⚡", title: "Blazing Fast", desc: "Measure sequential & random read/write speeds with multi-threaded precision." },
            { icon: "🖥️", title: "Cross-Platform", desc: "Runs on Windows, macOS, and Linux — wherever Java runs, JDiskMark runs." },
            { icon: "📊", title: "Rich Graphs", desc: "Visualize IOPS, bandwidth, and latency through interactive JFreeChart plots." },
            { icon: "🔓", title: "Open Source", desc: "Fully open source on GitHub — inspect, fork, and contribute freely." },
          ].map(({ icon, title, desc }) => (
            <div className='home-feature-card' key={title}>
              <span className='home-feature-card__icon'>{icon}</span>
              <h3 className='home-feature-card__title'>{title}</h3>
              <p className='home-feature-card__desc'>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── About ────────────────────────────────────────────── */}
      <section className='home-section home-section--about' id='about'>
        <div className='home-prose'>
          <span className='home-prose__label'>About</span>
          <h2 className='home-prose__heading'>What is JDiskMark?</h2>
          <p className='home-prose__body'>
            JDiskMark is a powerful and versatile disk benchmarking tool written in Java,
            designed to help you accurately measure the performance of your storage devices
            across different operating systems. Whether you're testing HDDs, SSDs, or network
            drives, JDiskMark provides reliable insights into your disk's read and write speeds.
          </p>
        </div>
      </section>

      {/* ── Download ─────────────────────────────────────────── */}
      <section className='home-section home-section--download home-section--alt' id='download'>
        <div className='home-prose'>
          <span className='home-prose__label'>Download</span>
          <h2 className='home-prose__heading'>Get JDiskMark</h2>
          <p className='home-prose__body'>
            Releases are published on our{' '}
            <a
              href='https://sourceforge.net/projects/jdiskmark/'
              target='_blank'
              rel='noopener noreferrer'
              className='home-link'
            >
              JDiskMark SourceForge project
            </a>
            . Download the latest installer for your platform and start benchmarking in minutes.
          </p>
          <a
            href='https://sourceforge.net/projects/jdiskmark/'
            target='_blank'
            rel='noopener noreferrer'
            className='home-dl-btn'
          >
            Go to SourceForge →
          </a>
        </div>
      </section>

      {/* ── Share / Join Our Community ────────────────────────── */}
      <section className='home-section home-section--community home-section--alt' id='community'>
        <div className='home-prose'>
          <span className='home-prose__label'>Share & Compare</span>
          <h2 className='home-prose__heading'>Join Our Community</h2>
          <p className='home-prose__body'>
            JDiskMark is more than a benchmarking tool — it's a growing community of
            hardware performance enthusiasts and professionals. Once you've benchmarked
            your drives, sign up to publish your results and share detailed benchmark
            reports and drive profiles with others around the world. Compare sequential
            and random read/write speeds, IOPS, and access times across a wide range of
            SSDs, HDDs, and NVMe devices contributed by real users.
          </p>
          <p className='home-prose__body'>
            As the platform grows, registered members will be able to unlock{' '}
            <strong>achievements</strong> and earn <strong>perks</strong> — rewarding
            active contributors who help build the most comprehensive public storage
            performance database available. Whether you're a power user pushing the limits
            of a Samsung 990 PRO or comparing budget drives for a home lab, your data
            matters here.
          </p>
          <button
            className='home-dl-btn'
            onClick={() => navigate("/sign_up")}
          >
            Sign Up Free →
          </button>
        </div>
      </section>

      {/* ── Contribute ─────────────────────────────────────── */}
      <section className='home-section home-section--contributing' id='contributing'>
        <div className='home-prose'>
          <span className='home-prose__label'>Contribute & Improve</span>
          <h2 className='home-prose__heading'>Join the Project</h2>
          <p className='home-prose__body'>
            JDiskMark is open source and welcomes contributions via the{' '}
            <a
              href='https://github.com/jDiskMark/jdm-java'
              target='_blank'
              rel='noopener noreferrer'
              className='home-link'
            >
              jdm-java GitHub repository
            </a>
            . Whether you're filing a bug report, submitting a pull request, or improving
            documentation — every contribution counts!
          </p>
          <a
            href='https://github.com/jDiskMark/jdm-java'
            target='_blank'
            rel='noopener noreferrer'
            className='home-dl-btn home-dl-btn--secondary'
          >
            View on GitHub →
          </a>
        </div>
      </section>

      {/* ── Help Us Grow ─────────────────────────────────────── */}
      <section className='home-section home-section--grow home-section--alt' id='grow'>
        <div className='home-prose'>
          <span className='home-prose__label'>Help Us Grow!</span>
          <h2 className='home-prose__heading'>Spread the Word</h2>
          <p className='home-prose__body'>
            Like our tool? Help spread the word about JDiskMark. We've created a standard
            8.5″ × 11″ poster that you can print, post, or share online.
          </p>
        </div>
        <div className='home-poster-wrapper'>
          <img
            src={`${REMOTE_BASE}/jdm-know-your-speed.png`}
            alt="Know Your Speed – JDiskMark printable poster"
            className='home-poster'
          />
        </div>
      </section>

      <BackToTop />
    </div>
  );
}